import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  loading: false,
  success: false,
  error: false,
  info: null,
};

// Thunks that own their own loading/error UX (popup-local submit state, dedicated
// loading fields in the ai/user slice, or fire-and-forget background polls). Excluded
// so they don't flip global ui flags and trigger unrelated page-level skeletons.
//
// Slice-level entries (no thunk name, just the slice prefix) exclude every action
// from that slice. The legacy `trade/` slice owns its own state.trade.loading and
// has been a separate slice since long before TRADE-901; letting the ui matcher
// double-catch its actions creates redundant flicker (ticker autocomplete keystroke
// dispatches, every Add/Update/Delete/Import/Export/Bulk trade flow, etc.).
const EXCLUDED_PREFIXES = [
  "ai/askMyJournal",
  "ai/indexUserData",
  "user/getPublicProfile",
  "user/checkUsernameAvailability",
  "user/reportBug",
  "social/getUnreadCount",
  "social/getNotifications",
  "social/markNotificationsRead",
  "social/markAllNotificationsRead",
  "feedback",
  "trade",
  // TRADE-973: study mutations are user-initiated, fast, and shouldn't
  // trigger Home's AnimatePresence to swap into the loading skeleton. Each
  // call site shows its own local loading state. The initial GET fetches
  // (getWatchlist / getStudyNote / getStudyNoteTickers) stay tracked because
  // they're part of the page-load hydration that the skeleton represents.
  "study/saveStudyNote",
  "study/deleteStudyNote",
  "study/addToWatchlist",
  "study/removeFromWatchlist",
];

const isUITracked = (type) =>
  !EXCLUDED_PREFIXES.some((p) => type.startsWith(p + "/"));

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    resetUI: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) => action.type?.endsWith("/pending") && isUITracked(action.type),
        (state) => {
          state.loading = true;
          state.success = false;
          state.error = false;
        }
      )
      .addMatcher(
        (action) => action.type?.endsWith("/fulfilled") && isUITracked(action.type),
        (state, action) => {
          state.loading = false;
          state.success = true;
          state.error = false;
          state.info = action.payload ?? null;
        }
      )
      .addMatcher(
        (action) => action.type?.endsWith("/rejected") && isUITracked(action.type),
        (state, action) => {
          state.loading = false;
          state.error = true;
          state.info = action.payload ?? null;
        });
  },
});

export const { resetUI } = uiSlice.actions;
export default uiSlice.reducer;
